import React from "react";
import "./index.scss";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  useCreateClientMutation,
  useUpdateClientMutation,
} from "store/client/clientApi";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import CustomizedButton from "@components/ButtonConfirm/ButtonConfirm";
import Delete from "@mui/icons-material/Delete"; // Or use your own `MoreActionsIcon`

const CreateClientComponent = ({ onClose, client }) => {
  const { t } = useTranslation();

  const currentUser = useSelector((state) => state.auth.user);

  const companyId = currentUser?.companyId?._id;

  const [createClient, { isLoading: isCreating }] = useCreateClientMutation();
  const [updateClient, { isLoading: isUpdating }] = useUpdateClientMutation();

  const { register, control, handleSubmit } = useForm({
    defaultValues: client
      ? {
          name: client.name,
          email: client.email,
          address: client.address,
          phone: client.phone,
          userClients: client?.userClients || [],
        }
      : undefined,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "userClients",
  });

  const onSubmit = async (data) => {
    try {
      const { name, email, address, phone, userClients } = data;
      const clientData = {
        name,
        email,
        address,
        phone,
        companyId,
        userClients,
      };

      await createClient(clientData).unwrap();
      toast.success(t("client.create_form.create_success"));
      onClose();
    } catch (error) {
      console.log("🚀 ~ onSubmit ~ error:", error);
      const errorMessage =
        error?.data?.message || error?.error || "Erreur inconnue";
      toast.error(errorMessage);
    }
  };

  const onUpdate = async (data) => {
    try {
      const { name, email, address, phone, userClients } = data;
      const clientData = {
        name,
        email,
        address,
        phone,
        userClients,
      };

      await updateClient({ id: client._id, data: clientData });
      toast.success(t("client.create_form.update_success"));
      onClose();
    } catch {
      toast.error(t("client.create_form.update_error"));
    }
  };

  return (
    <>
      <div className="row-container">
        <div className="input-container">
          <div className="input-title">{t("client.create_form.name")}</div>
          <input
            {...register("name", {
              required: "Le nom est requis",
            })}
            className="input-content"
            placeholder={t("client.create_form.name_placeholder")}
          />
        </div>
        <div className="input-container">
          <div className="input-title">{t("client.create_form.email")}</div>
          <input
            {...register("email", {
              required: "L'email est requis",
              pattern: {
                value: /^\S+@\S+$/i,
                message: "Adresse email invalide",
              },
            })}
            className="input-content"
            type="email"
            placeholder={t("client.create_form.email_placeholder")}
          />
        </div>
      </div>
      <div className="row-container">
        <div className="input-container">
          <div className="input-title">{t("client.create_form.address")}</div>
          <input
            {...register("address", {
              required: "L'addresse est requise",
            })}
            className="input-content"
            placeholder={t("client.create_form.address_placeholder")}
          />
        </div>
        <div className="input-container">
          <div className="input-title">{t("client.create_form.phone")}</div>
          <input
            {...register("phone", {
              required: "Le numéro est requis",
            })}
            className="input-content"
            type="phone"
            placeholder={t("client.create_form.phone_placeholder")}
          />
        </div>
      </div>
      <div>
        <div className="input-title">{t("client.user_clients")}</div>
        {fields.map((field, index) => (
          <>
            <div className="row-container" key={field.id}>
              <div className="input-container">
                <input
                  {...register(`userClients.${index}.name`, {
                    required: "Le nom est requis",
                  })}
                  placeholder="Nom"
                  className="input-content"
                />
              </div>
              <div className="input-container">
                <input
                  {...register(`userClients.${index}.email`, {
                    required: "L'email est requis",
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: "Adresse email invalide",
                    },
                  })}
                  type="email"
                  placeholder="Email"
                  className="input-content"
                />
              </div>
            </div>
            <div className="row-container" style={{ marginTop: "20px" }}>
              <div className="input-container">
                <input
                  {...register(`userClients.${index}.phone`, {
                    required: "Téléphone requis",
                  })}
                  placeholder="Téléphone"
                  className="input-content"
                />
              </div>
              <div className="input-container">
                <input
                  {...register(`userClients.${index}.address`, {
                    required: "L'adresse est requise",
                  })}
                  placeholder="Adresse"
                  className="input-content"
                />
              </div>
            </div>
            {!client ? (
              <div className="delete-button-container">
                <div role="button" onClick={() => remove(index)}>
                  <Delete sx={{ color: "#B71C1C" }} fontSize="medium" />
                </div>
              </div>
            ) : (
              <div className="separator" />
            )}
          </>
        ))}
        <CustomizedButton
          finalizeText={t("client.add_user")}
          handleCreate={() => {
            append({ name: "", email: "", phone: "", address: "" });
          }}
          bgColor={"linear-gradient(90deg, #1E88E5 0%, #1565C0 100%)"}
          className={"btn w-50"}
        />
      </div>

      <CustomizedButton
        creatingText={t(
          client ? "client.create_form.updating" : "client.create_form.creating"
        )}
        finalizeText={t(
          client ? "client.create_form.update" : "client.create_form.submit"
        )}
        handleCreate={() => {
          if (client) handleSubmit(onUpdate)();
          else handleSubmit(onSubmit)();
        }}
        isCreating={isCreating || isUpdating}
        className={"btn w-100"}
      />
    </>
  );
};

export default CreateClientComponent;
